import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { debounce, next, once } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { APIData, RequestError, Service } from 'webapp-types';
import { BlockVisualization, MetricType, ModelName, PopupEvent } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { copyObject, equal, isAlive } from 'webapp/utils/common';

import setField from 'webapp/helpers/set-field';
import validationMessage from 'webapp/helpers/validation-message';
import didUpdate from 'webapp/modifiers/did-update';
import BlockSingleComponent from 'webapp/components/block/single';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import CustomMetricsEditActionsScriptComponent from 'webapp/components/custom-metrics/edit/actions/script';
import FormInputComponent from 'webapp/components/form/input';
import FormLeaveConfirmPopupComponent from 'webapp/components/form/leave-confirm/popup';
import FormSelectComponent from 'webapp/components/form/select';
import FormSelectFormatComponent from 'webapp/components/form/select-format';
import MetricLibraryPopupsMetricBasicInfoTooltipComponent from 'webapp/components/metric-library/popups/metric-basic-info-tooltip';
import ScriptsFormCodeComponent from 'webapp/components/scripts/form/code';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type { SampleRecord } from 'webapp/models';
import BoardState from 'webapp/models-custom/board-state';
import DatawallGrid from 'webapp/models-custom/datawall-grid';
import DateRange from 'webapp/models-custom/date-range';
import MetricFromClient from 'webapp/models-custom/metric-from-client';
import type PopupModal from 'webapp/models-custom/popup/modal';
import type { ExpressionDetailItem } from 'webapp/models-custom/script';
import type Script from 'webapp/models-custom/script';
import StatusObject from 'webapp/models-custom/status-object';

import type { RSjqXHR } from 'webapp/services/request';

export type PreviewItemsData = Record<
	string, // scriptItemId
	{
		queriedValueHash: number;
		previewString: string;
	}
>;

const DefaultTimezoneValue = 'DEFAULT';

interface Args {
	popup: PopupModal;
	origin: string;
	model: Script;
	cloneScript: () => void;
	close: () => void;
	openMetricDefinitionPopupOnInit?: boolean;
	afterSave?: (model: Script, format: string) => void;
}

export default class ScriptsEditComponent extends Component<Args> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('constants') declare constantsService: Service.Constants;
	@service('request') declare requestService: Service.Request;
	@service('popup') declare popupService: Service.Popup;
	@service('logger') declare loggerService: Service.Logger;
	@service('model-manager/scripts') declare mmScriptsService: Service.ModelManager.Scripts;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('store') declare storeService: Service.Store;
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	@tracked submitInProgress = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.titleForEdit = this.model.description;

		this.setFormatValues();

		this.createBlockAndBoardState();

		this.args.popup.on(PopupEvent.CLOSING, this, () => {
			this.model?.rollback();
		});

		next(this, this.checkPreview);

		if (this.args.openMetricDefinitionPopupOnInit) {
			next(this, () => {
				this.openMetricDefinitionPopup();
			});
		}
	}

	get model() {
		return this.args.model;
	}

	get origin() {
		return this.args.origin;
	}

	// ------- Settings: Title ------

	@tracked titleForEdit = '';

	@action
	onTitleFocusOut() {
		const title = this.titleForEdit.trim();
		if (title) {
			this.model.description = title;
			this.model.validate(['description']);
			this.previewBlock.name = title;
		}
		this.titleForEdit = title;
	}

	// ------- Settings: Tooltip ------

	@cached
	get temporaryMetric() {
		const description = this.model.metricDefinition?.description,
			source = this.model.usedSources[0];

		if (!source || !description) {
			return null;
		}

		// reusing MetricFromClient bcos it does not require ED records
		return new MetricFromClient({
			id: 'temporary',
			key: 'temporary',
			name: this.titleForEdit,
			description,
			dataSourceTypeKey: source.sourceType.key,
			objectType: MetricType.METRIC_CALCULATION,
			sourceAvatar: source.realAvatar,
			sourceName: source.title,
			dimensions: [],
			isStandalone: true,
			hasUnits: false,
		});
	}

	// ------- Settings: Format ------

	@tracked formatValues: LabelValuePairString[] = [];

	setFormatValues() {
		this.formatValues = this.constantsService.formatOptions;

		const metricDefinitionFormat = this.model.metricDefinition?.format;
		if (!metricDefinitionFormat) {
			const { defaultFormat, defaultChangeFunction, defaultChangeFormat } =
				this.constantsService;
			const metricDefinition = {
				...this.model.metricDefinition,
				format: defaultFormat,
				changeFunction: defaultChangeFunction,
				changeFormat: defaultChangeFormat,
			};

			// avoiding same computation error
			next(() => {
				this.model.directPropUpdate({
					metricDefinition, // prevent format to be dirty property on init -> showing discard button when closing modal (when nothing edited)
				});
			});
		} else {
			this.addCustomFormat(metricDefinitionFormat);
		}
	}

	addCustomFormat(format: string) {
		if (!this.formatValues.find((item) => item.value === format)) {
			this.formatValues = [
				...this.formatValues,
				{
					value: format,
					label: format,
				},
			];
		}
	}

	@action
	changeFormat(value: string | null) {
		const format = value || '';
		this.model.metricDefinition = { ...this.model.metricDefinition, format };
		this.previewVariablesChanged();
		this.addCustomFormat(format);
	}

	// ------- Settings: Timezone ------

	get timezoneValues() {
		const values: LabelValuePairString[] = [
			{
				value: DefaultTimezoneValue,
				label: 'Default',
			},
		];
		values.push(...this.model.usedTimezones);
		return values;
	}

	get selectedTimezone() {
		return this.model.timezone || DefaultTimezoneValue;
	}

	set selectedTimezone(value: string) {
		let modelValue: string | null = null;
		if (value !== DefaultTimezoneValue) {
			modelValue = value;
		}
		this.model.timezone = modelValue;
	}

	@action
	changeTimezone(value: string) {
		this.selectedTimezone = value;
		this.previewVariablesChanged();
	}

	// ------- Actions ------

	afterSave(format: string) {
		if (this.args.afterSave) {
			this.args.afterSave(this.args.model, format);
		}
	}

	@action
	save() {
		if (this.submitInProgress) {
			return;
		}

		const model = this.model,
			isNew = model.isNew;

		if (!model.validate()) {
			return;
		}

		// remove excess / redundant brackets
		const updatedExpression = this.mmScriptsService.checkToRemoveExcessBrackets(
			model.expression,
			model.expressionDetails,
		);
		if (updatedExpression) {
			model.expression = updatedExpression;
		}

		this.submitInProgress = true;

		model
			.save()
			.then(() => {
				const eventData: APIData.Tracking.Data = { origin: this.origin };

				if (isNew) {
					const datasources: string[] = [];

					this.model.usedSources.forEach((source) => {
						const sourceTypeKey = source.sourceType?.key;

						if (sourceTypeKey && !datasources.includes(sourceTypeKey)) {
							datasources.push(sourceTypeKey);
						}
					});

					eventData.datasources = datasources;
				}

				this.infolayerService.ok(`Calculated metric ${isNew ? 'created!' : 'saved!'}`);
				this.analyticsService.event(
					isNew ? TrackingEvent.CALCULATION_CREATED : TrackingEvent.CALCULATION_EDITED,
					eventData,
				);

				// reset limitations on the script metrics
				model.outputMetrics.forEach((m) => {
					m.cleanupLimitationsCache();
				});

				this.afterSave(this.model.metricDefinition.format);

				next(() => {
					if (isNew && this.origin === 'listing') {
						this.model.openCreateBlockModal('script', true);
					}
					this.close();
				});
			})
			.finally(() => {
				if (isAlive(this)) {
					this.submitInProgress = false;
				}
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	@action
	delete() {
		void this.mmScriptsService
			.deleteScript(this.model)
			.then(() => {
				this.args.popup.offAll(PopupEvent.CLOSE_INTENT); // ignore active leave confirm
				this.submitInProgress = false; // so close isnt blocked
				this.close();
			})
			.catch();
	}

	@action
	openMetricDefinitionPopup() {
		let cleanupOnPopupClose = false;
		let outputMetric = this.model?.outputMetrics[0];

		if (!outputMetric) {
			outputMetric = this.mmMetricsService.createNewModel({
				objectType: MetricType.METRIC_CALCULATION,
				objectId: -1,
				spaceAccessId: this.model.sourceId,
				dataSourceTypeKey: 'Custom',
			});
			cleanupOnPopupClose = true;
		}

		outputMetric.name = this.model.description;
		outputMetric.description = this.model.metricDefinition.description;
		outputMetric.format = this.model.metricDefinition.format;
		outputMetric.changeFormat = this.model.metricDefinition.changeFormat;
		outputMetric.changeFunction = this.model.metricDefinition.changeFunction;
		outputMetric.changeInvert = this.model.metricDefinition.changeInvert;
		outputMetric.noReset = this.model.metricDefinition.noReset;

		const popup = this.mmMetricsService.openMetricDefinitionPopup(
			outputMetric,
			true,
			undefined,
			undefined,
			undefined,
			(metricDefinitionData) => {
				const { defaultFormat, defaultChangeFunction, defaultChangeFormat } =
					this.constantsService;

				this.model.description = metricDefinitionData.name;
				this.model.metricDefinition = {
					description: metricDefinitionData.description,
					format: metricDefinitionData.format || defaultFormat,
					changeFormat: metricDefinitionData.changeFormat || defaultChangeFormat,
					changeFunction: metricDefinitionData.changeFunction || defaultChangeFunction,
					changeInvert: metricDefinitionData.changeInvert,
					noReset: metricDefinitionData.noReset,
				};

				this.previewVariablesChanged();
			},
		);

		if (cleanupOnPopupClose) {
			popup.on(PopupEvent.CLOSED, this, () => {
				void outputMetric.delete(true);
			});
		}
	}

	@action
	checkIsDirty(): boolean {
		return this.model.isDirty;
	}

	@action
	close() {
		if (!this.submitInProgress) {
			this.args.close();
		}
	}

	// ------- Data preview: Block ------

	@tracked boardState!: BoardState;

	@cached
	get previewBlock() {
		return this.boardState.selectedBlock!;
	}

	@cached
	get previewBlockState() {
		return this.boardState.getBlockState(this.previewBlock)!;
	}

	@cached
	get metricLimitations() {
		return this.previewBlockState.limitationsCM!; // through block-state, to catch changes
	}

	get dateRange() {
		return this.metricLimitations.dateRange!;
	}

	get colorConfig() {
		const blockColor = this.constantsService.defaultBoardColor;
		return this.mmBoardsService.getColorConfig(blockColor, 1);
	}

	private createBlockAndBoardState() {
		const params = {
			id: this.mmScriptsService.generateTemporaryFakeId(),
			name: this.model.description,
			order: 0,
			width: DatawallGrid.minimumSize * 4, // for blockSizeCoeff (fontsizes)
			height: DatawallGrid.minimumSize * 4, // for blockSizeCoeff (fontsizes)
			visualization: BlockVisualization.BAR_CHART,
		};

		const block = this.mmBlocksService.createNewModel(params);
		const boardState = BoardState.createForSingleBlock(block);
		boardState.isViewOnly = false;
		boardState.explicitlyDisableAllPreviews = false;
		boardState.onStateChange = (payload) => {
			const changes = payload.blockPreviews?.[0]?.changes;
			if (!changes) {
				return;
			}
			if (changes.dateRange) {
				this.previewVariablesChanged();
			} else if (changes.settings?.visualization) {
				// bcos change in visualization without change in daterange keeps the data
				// we apply the visualization change directly on the block
				// where it will get picked up by wrapper:checkToRecreateVisualization
				this.previewBlock.visualization = changes.settings.visualization;
			}
		};
		this.boardState = boardState;

		const blockSetup = block.blockSetup;
		blockSetup.isAutosaveOn = false;
	}

	override willDestroy() {
		super.willDestroy();

		// remove preview block and its dataselets
		const fakeBlock = this.boardState?.selectedBlockState?.block;
		if (fakeBlock) {
			void fakeBlock.delete(true);
		}

		// cleanup of "item dataselects" will happen on popup close (see custom-metrics/edit/popup)
	}

	// ------- Data preview: Request & Sample ------

	@tracked previewIsLoading = true;
	@tracked previewItemsData: PreviewItemsData = {};

	previewLoadingXHR: RSjqXHR | null = null;

	queryDataPreview() {
		const { expression, expressionDetails } = this.model;

		if (!expression) {
			return;
		}

		const data: APIData.DataCalculation.PreviewRequest = {
			expression,
			expressionDetails,
			metricDefinition: this.model.metricDefinition,
			timezone: this.model.timezone,
			block: this.previewBlock.toSerializedJSON(true),
		};

		// only add dateRange after first request when we already have limitations
		if (this.metricLimitations.rawLimitations) {
			data.dateRange = this.metricLimitations.dateRange?.formula;
		}

		if (this.model.numericId) {
			data.id = this.model.numericId;
		}

		if (this.isSameAsLastQueried(data)) {
			return;
		}

		// if request already in progress, cancel it
		const previewLoadingXHR = this.previewLoadingXHR;
		if (previewLoadingXHR && previewLoadingXHR.readyState !== 4) {
			previewLoadingXHR.abort();
		}

		const previewBlockId = this.previewBlock.numericId!;

		// empty sample to show loader
		(
			this.storeService.peekRecord(ModelName.SAMPLE, previewBlockId) as SampleRecord
		)?.unloadRecord();

		// for the first ever request, create one fake DS bcos of the loader logic
		if (!this.mmDataSelectsService.getRecordsForBlock(previewBlockId).length) {
			this.storeService.pushPayload({
				dataSelect: {
					id: this.mmScriptsService.generateTemporaryFakeId(),
					blockId: previewBlockId,
					dateRange: this.metricLimitations.dateRange?.formula,
				},
			});
		}

		const { itemsPart1, itemsPart2 } = this.mmScriptsService.createScriptItems(
			expression,
			expressionDetails,
		);
		const itemsFromCode = [...itemsPart1, ...itemsPart2];

		const previousPreviewItemsData = this.previewItemsData;
		const previewItemsData: PreviewItemsData = {};
		itemsFromCode.forEach((item) => {
			if (item.id) {
				item.queriedValueHash = item.valueHash;
				const previousPreviewData = previousPreviewItemsData[item.id];
				if (previousPreviewData?.queriedValueHash === item.valueHash) {
					// nothing changed on this item, reuse preview data
					previewItemsData[item.id] = previousPreviewData;
				}
			}
		});
		this.previewItemsData = previewItemsData;

		this.previewIsLoading = true;
		this._lastQueriedValues = copyObject(data);

		const promise = this.requestService.api<APIData.DataCalculation.PreviewResponse>(
			'POST',
			'/scripts/preview',
			data,
			{
				timeout: 5 * 60 * 1000,
			},
		);
		// promise = this.request.api('GET', '/scripts');
		this.previewLoadingXHR = promise.jqPromise as RSjqXHR;

		promise
			.then(
				(reply) => {
					if (!isAlive(this)) {
						return;
					}

					// // TEST DATA STARTS HERE
					// reply = window.dbx_mock['POST /d/scripts/preview'];

					// const dateRange = data.dateRange || reply.dateRange;
					// reply.dateRange = dateRange;

					// const itemValueMock = reply.oaizaynu7c;
					// itemsFromCode.forEach((item) => {
					// 	reply[item.id!] = itemValueMock;
					// });

					// if (reply.dataSelects) {
					// 	reply.dataSelects.forEach((dataSelect) => {
					// 		dataSelect.blockId = previewBlockId;
					// 		dataSelect.dateRange = dateRange;
					// 	});
					// }
					// if (reply.sample) {
					// 	reply.sample.id = previewBlockId;
					// 	reply.sample.sampledata.dateRange = dateRange;
					// }
					// // TEST DATA ENDS HERE

					if (reply.limitations) {
						this.metricLimitations.rawLimitations = reply.limitations.ranges;
					} else {
						this.loggerService.logError(
							'[CDR][scripts] Preview did not return limitations',
							{ scriptId: this.model.id },
						);
					}
					if (reply.dateRange) {
						if (
							!data.dateRange ||
							!DateRange.createFromFormula(data.dateRange).isEqual(reply.dateRange)
						) {
							this.metricLimitations.updateDateRangeFromFormula(reply.dateRange);
							this.previewBlockState.limitationsCM!.updateDateRangeFromFormula(
								reply.dateRange,
							);
						}

						if (this._lastQueriedValues && !this._lastQueriedValues.dateRange) {
							// using getFormula instead of reply.dateRange because of object key order
							this._lastQueriedValues.dateRange =
								this.metricLimitations.dateRange?.formula;
						}
					} else {
						this.loggerService.logError(
							'[CDR][scripts] Preview did not return dateRange',
							{ scriptId: this.model.id },
						);
					}

					// cleanup previous dataSelects
					this.mmDataSelectsService
						.getRecordsForBlock(previewBlockId)
						.forEach((record) => record.unloadRecord());

					// and add new ones
					if (reply.dataSelects) {
						this.storeService.pushPayload({
							dataSelects: reply.dataSelects,
						});
					}

					// and add data
					if (reply.sample) {
						this.storeService.pushPayload({ sample: reply.sample });
					}

					this.previewIsLoading = false;

					const formatPreviewFn = (r: APIData.DataCalculation.PreviewItemValue) => {
						let s = r.valueStr || '-';
						if (r.attribute) {
							s += ' (' + r.attribute + ')';
						}
						return s;
					};

					const keysFromResponse: Partial<APIData.DataCalculation.PreviewResponse> =
						Object.assign({}, reply);
					delete keysFromResponse.sample;
					delete keysFromResponse.block;
					delete keysFromResponse.dateRange;
					delete keysFromResponse.limitations;
					delete keysFromResponse.dataSelects;

					const previewItemsData: PreviewItemsData = {};
					Object.keys(keysFromResponse).forEach((key) => {
						const item = itemsFromCode.find((item) => item.id === key);
						if (item) {
							previewItemsData[key] = {
								queriedValueHash: item.queriedValueHash,
								previewString:
									keysFromResponse[key]!.map(formatPreviewFn).join(' / ') || '-',
							};
						}
					});
					this.previewItemsData = previewItemsData;

					if (keysFromResponse.error?.message) {
						this.infolayerService.error(keysFromResponse.error.message);
					}

					const statusObjects = StatusObject.createFromDataArray(
						keysFromResponse?.statusObjects,
					);

					if (statusObjects.length) {
						this.infolayerService.statusObjects(statusObjects);
					}
				},
				(errorData: RequestError) => {
					const statusText = errorData.obj?.statusText;

					// ignore only aborted
					if (statusText === 'abort') {
						return;
					}

					if (isAlive(this)) {
						const previewItemsData: PreviewItemsData = {};
						itemsFromCode.forEach((item) => {
							if (item.id) {
								previewItemsData[item.id] = {
									queriedValueHash: item.queriedValueHash,
									previewString: '-',
								};
							}
						});
						this.previewItemsData = previewItemsData;
						this.previewIsLoading = false;
					}

					throw errorData;
				},
			)
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	private _lastQueriedValues: APIData.DataCalculation.PreviewRequest | null = null;

	private isSameAsLastQueried(data: APIData.DataCalculation.PreviewRequest) {
		const lastQueried = this._lastQueriedValues;

		return (
			lastQueried &&
			equal(data.metricDefinition, lastQueried.metricDefinition) &&
			equal(data.expression, lastQueried.expression) &&
			equal(data.expressionDetails, lastQueried.expressionDetails, true) &&
			data.timezone === lastQueried.timezone &&
			data.id === lastQueried.id &&
			equal(data.dateRange, lastQueried.dateRange) &&
			equal(data.block, lastQueried.block)
		);
	}

	private _prevDR = '';
	private _prevMetricDefinition = '';
	private _prevTimezone: string | null = null;

	private previewVariablesChanged() {
		if (!this.model) {
			return;
		}

		const previousDateRange = this._prevDR,
			currentDateRange = this.metricLimitations.dateRange;
		const isDifferentDateRange =
			previousDateRange !== JSON.stringify(currentDateRange?.formula);
		if (
			isDifferentDateRange ||
			this._prevMetricDefinition !== JSON.stringify(this.model.metricDefinition) ||
			this._prevTimezone !== this.model.timezone
		) {
			if (currentDateRange && !currentDateRange.isEmpty) {
				this._prevDR = JSON.stringify(currentDateRange.formula);
			} else {
				this._prevDR = '';
			}
			this._prevMetricDefinition = JSON.stringify(this.model.metricDefinition);
			this._prevTimezone = this.model.timezone;
			if (isDifferentDateRange && !!previousDateRange) {
				// must re-request all, but skip first request after limitations
				this.previewItemsData = {};
			}
			debounce(this, this.checkPreview, 150);
		}
	}

	private checkTimezone() {
		const timezone = this.model.timezone;
		if (timezone && !this.timezoneValues.find((item) => item.value === timezone)) {
			this.selectedTimezone = DefaultTimezoneValue;
		}
	}

	@action
	onDateRangeChange() {
		this.previewVariablesChanged();
	}

	@action
	checkPreview() {
		if (!this.model.expression) {
			return;
		}
		this.queryDataPreview();
	}

	@action
	updateExpression(expression: string, expressionDetails: ExpressionDetailItem[]) {
		this.model.expression = expression;
		this.model.expressionDetails = expressionDetails;
		once(this, this.checkTimezone);
		once(this, this.checkPreview);
	}
	<template>
		<div class="form form--script theme-dark tw:dark">
			<div class="form__top">

				<div class="form__setup">
					{{#if this.model.errors.expression}}
						<div class="error">{{validationMessage this.model.errors.expression}}</div>
					{{/if}}
					<ScriptsFormCodeComponent
						@model={{this.model}}
						@previewItemsData={{this.previewItemsData}}
						@defaultDateRange={{this.dateRange}}
						@updateExpression={{this.updateExpression}}
					/>
				</div>

				<div
					class="form__preview"
					{{didUpdate this.onDateRangeChange this.metricLimitations.dateRange.formula}}
				>

					<CustomMetricsEditActionsScriptComponent
						@model={{this.model}}
						@duplicate={{@cloneScript}}
						@delete={{this.delete}}
					/>

					{{#if this.boardState}}
						<div class="view-single-block-wrapper">
							<BlockSingleComponent
								@boardState={{this.boardState}}
								@colorConfig={{this.colorConfig}}
								@showBg={{true}}
								@extraClasses={{unless this.previewBlock.sample "loading"}}
							/>
						</div>
					{{/if}}

					<div class="row two-in-line">
						<div class="left">
							<FormSelectFormatComponent
								name="format"
								@selected={{this.model.metricDefinition.format}}
								@options={{this.formatValues}}
								@onChange={{this.changeFormat}}
								@onAdd={{this.changeFormat}}
								@isDisabled={{this.submitInProgress}}
								@isDropdownWider={{true}}
							/>
						</div>
						<div class="right">
							<FormSelectComponent
								name="timezone"
								@selected={{this.selectedTimezone}}
								@options={{this.timezoneValues}}
								@placeholder="Time zone"
								@isRequired={{true}}
								@isNumeric={{false}}
								@isDisabled={{this.submitInProgress}}
								@isDropdownWider={{true}}
								@onChange={{this.changeTimezone}}
							/>
						</div>
					</div>

					<div class="row edit-metric-link">
						<a role="button" {{on "click" this.openMetricDefinitionPopup}}>
							<CommonUiIconComponent @name="Settings" @size="sm" />
							Edit metric definition
						</a>
					</div>

				</div>
			</div>

			<div class="form__bottom">
				<div class="name">
					<FormInputComponent
						name="name"
						@value={{this.titleForEdit}}
						placeholder="Metric name"
						@focusOut={{this.onTitleFocusOut}}
						@loseFocusOnEnter={{true}}
						maxlength="50"
						@onEmpty={{setField target=this property="titleForEdit"}}
						class="unfocusable"
					/>

					{{#if this.temporaryMetric}}
						<MetricLibraryPopupsMetricBasicInfoTooltipComponent
							@model={{this.temporaryMetric}}
							@mouseLeaveDelay={{500}}
							@theme="dark"
						>
							<CommonUiIconComponent @name="InfoFill" @size="sm" />
						</MetricLibraryPopupsMetricBasicInfoTooltipComponent>
					{{/if}}
				</div>

				<div class="controls2">
					<div class="controls2__left">
						{{! "cancel" class is required for leave-confirm logic }}
						<SubmitButtonComponent
							@text="Cancel"
							@theme="grey-empty"
							@extraClass="cancel"
							@onTrigger={{this.close}}
						/>
					</div>
					<div class="controls2__right">
						<FormLeaveConfirmPopupComponent
							@popup={{@popup}}
							@save={{this.save}}
							@checkIsDirty={{this.checkIsDirty}}
							as |controls|
						>
							<controls.save
								@text="Save"
								@extraClass="save"
								@submitInProgress={{this.submitInProgress}}
							/>
						</FormLeaveConfirmPopupComponent>
					</div>
				</div>
			</div>
		</div>
	</template>
}
